import React from 'react';
import {
  Grid,
  Box,
  Button
} from '@material-ui/core';
import { TextField } from '@material-ui/core';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import { makeStyles } from '@material-ui/core/styles';
import CliniqueService from '../services/CliniqueService';

// Icônes Material-UI
import AddIcon from '@material-ui/icons/Add';
import BusinessIcon from '@material-ui/icons/Business';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import LocationOnIcon from '@material-ui/icons/LocationOn';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    marginTop: theme.spacing(1),
  },
  gridItem: {
    padding: theme.spacing(1),
  },
  iconAdornment: {
    color: '#226D68',
    marginRight: theme.spacing(1),
  },
  submitButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 40px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(34, 109, 104, 0.3)',
    textTransform: 'none',
    width: '100%',
    marginTop: theme.spacing(2),
    '&:hover': {
      background: 'linear-gradient(135deg, #1a5a55 0%, #226D68 100%)',
      boxShadow: '0 6px 20px rgba(34, 109, 104, 0.4)',
    },
    '&:disabled': {
      background: '#cbd5e1',
      boxShadow: 'none',
    },
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  formTitle: {
    fontWeight: 700,
    color: '#226D68',
    marginBottom: theme.spacing(1),
    fontSize: '1.5rem',
  },
  formSubtitle: {
    color: '#64748b',
    fontSize: '0.9rem',
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const FournisseurForm = ({ onClose, refresh }) => {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);

  const initialValues = {
    nomFournisseur: '', 
    phone1: '', 
    phone2: '', 
    email: '', 
    adresse: '', 
  };

  const validationSchema = Yup.object().shape({
    nomFournisseur: Yup.string().min(2, "Trop court").required("Requis"),
    phone1: Yup.string().matches(/^[0-9]+$/, "Numéro invalide").required("Requis"),
    phone2: Yup.string().matches(/^[0-9]+$/, "Numéro invalide"),
    email: Yup.string().email("Email invalide").required("Requis"),
    adresse: Yup.string().min(5, "Adresse trop courte").required("Requis"),
  });

  const onSubmit = async (values, props) => {
    try {
      const fournisseur = {
        "nomFournisseur": values.nomFournisseur.trim(),
        "phone1": values.phone1.trim(),
        "phone2": values.phone2.trim(),
        "email": values.email.trim(),
        "speudo": auth.nom,
        "adresse": values.adresse.trim(),
      };

      await CliniqueService.createFournisseur(fournisseur);
      
      toast.success("Fournisseur ajouté avec succès", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      
      props.resetForm();
      
      // Fermer le dialog et rafraîchir si nécessaire
      if (onClose) onClose();
      if (refresh) refresh();
      
    } catch (error) {
      console.error('Erreur ajout fournisseur:', error);
      toast.error("❌ Erreur lors de l'ajout du fournisseur", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <Box>
      {/* En-tête du formulaire */}
      <Box className={classes.formHeader}>
        <BusinessIcon style={{ fontSize: 40, color: '#226D68', marginBottom: 8 }} />
        <div className={classes.formTitle}>Nouveau Fournisseur</div>
        <div className={classes.formSubtitle}>Ajoutez un nouveau fournisseur à votre réseau</div>
      </Box>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {(props) => (
          <Form noValidate className={classes.formContainer}>
            <Grid container spacing={2}>
              
              {/* Informations générales */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <BusinessIcon /> Informations générales
                </div>
              </Grid>

              {/* Nom du fournisseur */}
              <Grid item xs={12} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name='nomFournisseur' 
                  label='Nom du fournisseur' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  error={props.errors.nomFournisseur && props.touched.nomFournisseur}
                  helperText={<ErrorMessage name='nomFournisseur' />} 
                  required 
                  InputProps={{
                    startAdornment: <BusinessIcon className={classes.iconAdornment} />
                  }}
                  placeholder="Ex: PharmaDistrib, MedSupply, etc."
                />
              </Grid>

              {/* Coordonnées */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <PhoneIcon /> Coordonnées
                </div>
              </Grid>

              {/* Téléphones */}
              <Grid item xs={12} md={6} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name='phone1' 
                  label='Téléphone principal' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  error={props.errors.phone1 && props.touched.phone1}
                  helperText={<ErrorMessage name='phone1' />} 
                  required 
                  InputProps={{
                    startAdornment: <PhoneIcon className={classes.iconAdornment} />
                  }}
                  placeholder="Ex: 771234567"
                />
              </Grid>

              <Grid item xs={12} md={6} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name='phone2' 
                  label='Téléphone secondaire' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  error={props.errors.phone2 && props.touched.phone2}
                  helperText={<ErrorMessage name='phone2' />}
                  InputProps={{
                    startAdornment: <PhoneIcon className={classes.iconAdornment} />
                  }}
                  placeholder="Ex: 761234567 (optionnel)"
                />
              </Grid>

              {/* Email et Adresse */}
              <Grid item xs={12} md={6} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name='email' 
                  label='Email' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  type="email"
                  error={props.errors.email && props.touched.email}
                  helperText={<ErrorMessage name='email' />} 
                  required 
                  InputProps={{
                    startAdornment: <EmailIcon className={classes.iconAdornment} />
                  }}
                  placeholder="exemple@fournisseur.com"
                />
              </Grid>

              <Grid item xs={12} md={6} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name='adresse' 
                  label='Adresse' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  error={props.errors.adresse && props.touched.adresse}
                  helperText={<ErrorMessage name='adresse' />} 
                  required 
                  InputProps={{
                    startAdornment: <LocationOnIcon className={classes.iconAdornment} />
                  }}
                  placeholder="Adresse complète du fournisseur"
                />
              </Grid>

              {/* Bouton de soumission */}
              <Grid item xs={12} className={classes.gridItem}>
                <Button 
                  type='submit' 
                  variant='contained'
                  className={classes.submitButton}
                  startIcon={<AddIcon />}
                  disabled={props.isSubmitting}
                >
                  {props.isSubmitting ? 'Ajout en cours...' : 'Ajouter le fournisseur'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default FournisseurForm;